import { Injectable } from '@angular/core';
import { TimeEntriesStore } from './time-entries.store';
import { TimeEntriesQuery } from './time-entries.query';
import { TimeEntryBackendService } from '../../../services/time-entry/time-entry-backend.service';
import { map, tap } from 'rxjs/operators';
import { TimeEntryListItem } from '../../../models/time-entry/time-entry-list-item';
import { TinyColor } from '@ctrl/tinycolor';
import { DateRange } from '../../../models/date-range';
import { SessionService } from '../../session/session.service';
import {
  TimeEntryListParamter,
  TimeEntryListParamterAdmin,
  TimelineWorkPeriodDataModel,
} from '../../../models/client-models';
import { Observable } from 'rxjs';
import { ToastService } from '../../../services/toast-service';

@Injectable({
  providedIn: 'root',
})
export class TimeEntriesService {
  constructor(
    private timeEntriesStore: TimeEntriesStore,
    private timeEntriesQuery: TimeEntriesQuery,
    private sessionService: SessionService,
    private timeEntryBackendService: TimeEntryBackendService,
    private toastService: ToastService
  ) {}

  getTimeEntriesForRange(
    dateRange: DateRange,
    personID: number,
    timeEntryId: number = 0
  ) {
    this.clearState();
    this.getTimeEntriesForRangeNext(dateRange, personID, timeEntryId);
  }

  setLoading(loading: boolean) {
    this.timeEntriesStore.setLoading(loading);
  }

  getTimeEntriesForRangeAsAdmin(
    dateRange: DateRange,
    personID: number,
    admiaUserID: number,
    timeEntryID: number = 0
  ) {
    this.clearState();
    this.getTimeEntriesAsAdminNext(
      dateRange,
      personID,
      admiaUserID,
      timeEntryID
    );
  }

  getTimeEntriesAsAdminNext(
    dateRange: DateRange,
    personID: number,
    admiaUserID: number,
    timeEntryID: number = 0
  ) {
    this.timeEntriesStore.setLoading(true);
    const nextPage = this.timeEntriesQuery.getValue()?.page + 1;
    this.setPage(nextPage);
    const parameter = {
      admiaUserID,
      personID: personID,
      start: dateRange.start,
      end: dateRange.end,
      pageNumber: nextPage,
      pageSize: this.timeEntriesQuery.getValue()?.pageSize,
    } as TimeEntryListParamterAdmin;
    this.timeEntryBackendService
      .getTimeEntriesAsAdmin(parameter)
      .pipe(tap(() => this.timeEntriesStore.setLoading(false)))
      .subscribe((data) => {
        data?.timeEntryList.forEach((time) => {
          if (time.color !== '') {
            time.colorDark = new TinyColor(time.color).isDark();
          }
        });

        this.setAdminEdit(data?.comeAndGoEdit);
        this.timeEntriesStore.upsertMany(data.timeEntryList);
        this.timeEntriesStore.update((state) => ({
          ...state,
          total: data?.total,
        }));

        if (timeEntryID && timeEntryID > 0) {
          this.setActive(this.timeEntriesQuery.getEntity(timeEntryID));
        }

        if (data.timeEntryList.length < 50) {
          // if no results set page to previous.
          this.setPage(nextPage - 1);
          // set last page indicator.
          this.setLastPage(true);
        }
      });
  }

  /**
   * Select timeentries with date filter.
   *
   * @param dateRange Selected Date range.
   * @param personID
   */
  getTimeEntriesForRangeNext(
    dateRange: DateRange,
    personID: number,
    timeEntryId: number = 0
  ): void {
    this.timeEntriesStore.setLoading(true);

    const nextPage = this.timeEntriesQuery.getValue()?.page + 1;
    this.setPage(nextPage);

    const parameter = {
      personID: personID,
      start: dateRange.start,
      end: dateRange.end,
      pageNumber: nextPage,
      pageSize: this.timeEntriesQuery.getValue()?.pageSize,
    } as TimeEntryListParamter;

    // Api request and store update.
    this.timeEntryBackendService
      .getTimeEntries(parameter)
      .pipe(tap(() => this.timeEntriesStore.setLoading(false)))
      .subscribe({
        next: (data) => {
          data?.timeEntryList.forEach((time) => {
            if (time.color != '') {
              time.colorDark = new TinyColor(time.color).isDark();
            }
          });
          this.timeEntriesStore.upsertMany(data.timeEntryList);
          this.timeEntriesStore.update((state) => ({
            ...state,
            total: data?.total,
          }));

          if (timeEntryId && timeEntryId > 0) {
            this.setActive(this.timeEntriesQuery.getEntity(timeEntryId));
          }

          this.sessionService.setComeAndGoEdit(data.comeAndGoEdit);

          if (data.timeEntryList.length < 50) {
            // if no results set page to previous.
            this.setPage(nextPage - 1);
            // set last page indicator.
            this.setLastPage(true);
          }
        },
        error: (error) => this.timeEntriesStore.setError(error.error),
      });
  }

  /**
   * Set Page which is loaded.
   */
  setPage(nextPage: number) {
    this.timeEntriesStore.update((state) => ({
      ...state,
      page: nextPage,
    }));
  }

  setAdminEdit(edit: boolean) {
    this.timeEntriesStore.update((state) => ({
      ...state,
      adminEditPermission: edit,
    }));
  }

  setLastPage(lastPage: boolean): void {
    this.timeEntriesStore.update((state) => ({
      ...state,
      lastPage,
    }));
  }

  setActive(item: TimeEntryListItem) {
    this.timeEntriesStore.setActive(item.timeEntryID);
  }

  clearState() {
    this.setPage(0);
    this.setLastPage(false);
    this.timeEntriesStore.remove();
    this.setLoading(false);
  }

  deleteAfterWorkPeriodChange(
    item: TimelineWorkPeriodDataModel
  ): Observable<boolean> {
    this.timeEntriesStore.setLoading(true);

    return this.timeEntryBackendService.deleteAfterWorkPeriodChange(item).pipe(
      tap(() => this.timeEntriesStore.setLoading(false)),
      map((data) => data.successful)
    );
  }

  hasTimeEntries(
    dateRange: DateRange,
    personalID: number
  ): Observable<boolean> {
    this.timeEntriesStore.setLoading(true);

    const parameter = {
      personID: personalID,
      start: dateRange.start,
      end: dateRange.end,
      pageNumber: 1,
      pageSize: this.timeEntriesQuery.getValue()?.pageSize,
    } as TimeEntryListParamter;

    return this.timeEntryBackendService.getTimeEntries(parameter).pipe(
      tap(() => this.timeEntriesStore.setLoading(false)),
      map((data) => !!data && data.timeEntryList.length > 0)
    );
  }
}
